import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Button, Table, Form, Alert, Tabs, Tab, Badge } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import ProtectedSection from '../components/ProtectedSection';
import moncashFeeService from '../services/moncashFeeService';

const KINDS = [
  { key: 'DEPOSIT', label: 'Dépôt' },
  { key: 'WITHDRAWAL', label: 'Retrait' },
];

function toInt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
}

function normalizeRows(rows) {
  return (rows || []).map(r => ({
    id: r.id || null,
    minAmount: r.minAmount ?? '',
    maxAmount: (r.maxAmount === null || r.maxAmount === undefined) ? '' : r.maxAmount,
    feeHTG: r.feeHTG ?? 0,
    createdByUser: r.createdByUser,
    modifiedByUser: r.modifiedByUser,
    modifiedAt: r.modifiedAt,
  }));
}

function localValidate(rows) {
  if (!rows || rows.length === 0) return 'Au moins une tranche est requise.';

  const parsed = rows.map((r, i) => {
    const min = toInt(r.minAmount);
    const max = r.maxAmount === '' ? null : toInt(r.maxAmount);
    const fee = toInt(r.feeHTG);

    if (min === null || min < 0) return { error: `minAmount invalide (ligne ${i + 1})` };
    if (max !== null && max < min) return { error: `maxAmount invalide (ligne ${i + 1})` };
    if (fee === null || fee < 0) return { error: `feeHTG invalide (ligne ${i + 1})` };

    return { min, max, fee };
  });

  const err = parsed.find(x => x.error)?.error;
  if (err) return err;

  const sorted = parsed.map(p => ({ min: p.min, max: p.max, fee: p.fee }))
    .sort((a, b) => a.min - b.min);

  if (sorted[0].min !== 0) return `Intervalle manquant: 0 à ${sorted[0].min - 1}`;

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];

    if (prev.max === null) return 'La tranche illimitée (max vide) doit être la dernière.';
    if (curr.min <= prev.max) return `Chevauchement: ${curr.min} commence avant ${prev.max}.`;

    const expected = prev.max + 1;
    if (curr.min !== expected) return `Intervalle manquant: ${expected} à ${curr.min - 1}`;
  }

  return '';
}

export default function MoncashFeesPage() {
  const { hasRight } = useContext(AuthContext);

  const canView = hasRight('MONCASH_FEE_VIEW');
  const canUpdate = hasRight('MONCASH_FEE_UPDATE');

  const [activeKind, setActiveKind] = useState('DEPOSIT');
  const [rows, setRows] = useState([]);
  const [serverError, setServerError] = useState('');
  const [info, setInfo] = useState('');

  const fetchKind = (kind) => {
    if (!canView) return;
    setServerError('');
    setInfo('');

    moncashFeeService.list(kind)
      .then((res) => {
        setRows(normalizeRows(res?.rules || []));
        setInfo('');
      })
      .catch((e) => {
        setRows([]);
        setServerError(e?.response?.data?.message || 'Erreur chargement frais MonCash.');
      });
  };

  useEffect(() => {
    fetchKind(activeKind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKind, canView]);

  const localError = useMemo(() => localValidate(rows), [rows]);
  const canSave = canUpdate && !localError;

  const addRow = () => {
    setRows((prev) => [...prev, { id: null, minAmount: '', maxAmount: '', feeHTG: 0 }]);
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const save = async () => {
    setServerError('');
    setInfo('');

    // Build payload as backend expects
    const payloadRules = rows.map(r => ({
      minAmount: toInt(r.minAmount),
      maxAmount: r.maxAmount === '' ? null : toInt(r.maxAmount),
      feeHTG: toInt(r.feeHTG) ?? 0,
    }));

    try {
      const res = await moncashFeeService.save(activeKind, payloadRules);
      setRows(normalizeRows(res?.rules || []));
      setInfo('Barème enregistré.');
    } catch (e) {
      setServerError(e?.response?.data?.message || 'Erreur sauvegarde barème.');
    }
  };

  const lastModified = useMemo(() => {
    const dates = rows
      .map(r => r.modifiedAt)
      .filter(Boolean)
      .map(d => new Date(d).getTime())
      .filter(n => Number.isFinite(n));
    if (dates.length === 0) return null;
    return new Date(Math.max(...dates));
  }, [rows]);

  return (
    <Container className="pt-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2>Frais MonCash</h2>

        <ProtectedSection code="MONCASH_FEE_UPDATE">
          <Button onClick={save} disabled={!canSave}>Enregistrer</Button>
        </ProtectedSection>
      </div>

      {lastModified && (
        <div className="text-muted small mb-2">
          Dernière modification: {lastModified.toLocaleString()}
        </div>
      )}

      {!canView && (
        <Alert variant="warning">Vous n’avez pas le droit de voir les frais MonCash.</Alert>
      )}

      {serverError && <Alert variant="danger">{serverError}</Alert>}
      {info && <Alert variant="success">{info}</Alert>}
      {localError && <Alert variant="warning">{localError}</Alert>}

      <Tabs
        activeKey={activeKind}
        onSelect={(k) => setActiveKind(k)}
        className="mb-3"
      >
        {KINDS.map(k => (
          <Tab key={k.key} eventKey={k.key} title={k.label} />
        ))}
      </Tabs>

      <Row className="mb-2">
        <Col className="d-flex gap-2 align-items-center">
          <Badge bg="secondary">Règle</Badge>
          <span className="text-muted small">
            Les intervalles doivent commencer à 0, être continus, sans chevauchement.
            Le dernier max peut être vide (illimité).
          </span>
        </Col>
        <Col className="d-flex justify-content-end">
          <ProtectedSection code="MONCASH_FEE_UPDATE">
            <Button variant="outline-secondary" onClick={addRow}>+ Ajouter une tranche</Button>
          </ProtectedSection>
        </Col>
      </Row>

      <Table hover responsive>
        <thead>
          <tr>
            <th style={{ width: 120 }}>Min</th>
            <th style={{ width: 120 }}>Max</th>
            <th style={{ width: 140 }}>Frais (HTG)</th>
            <th>Audit</th>
            <th style={{ width: 110 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx}>
              <td>
                <Form.Control
                  type="number"
                  step="1"
                  value={r.minAmount}
                  onChange={(e) => updateRow(idx, { minAmount: e.target.value })}
                  disabled={!canUpdate}
                />
              </td>
              <td>
                <Form.Control
                  type="number"
                  step="1"
                  placeholder="(vide = ∞)"
                  value={r.maxAmount}
                  onChange={(e) => updateRow(idx, { maxAmount: e.target.value })}
                  disabled={!canUpdate}
                />
              </td>
              <td>
                <Form.Control
                  type="number"
                  step="1"
                  value={r.feeHTG}
                  onChange={(e) => updateRow(idx, { feeHTG: e.target.value })}
                  disabled={!canUpdate}
                />
              </td>
              <td className="text-muted small">
                <div>Créé: {r.createdByUser?.username || '—'}</div>
                <div>Modifié: {r.modifiedByUser?.username || '—'}</div>
                <div>{r.modifiedAt ? new Date(r.modifiedAt).toLocaleString() : '—'}</div>
              </td>
              <td>
                <ProtectedSection code="MONCASH_FEE_UPDATE">
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeRow(idx)}
                  >
                    Supprimer
                  </Button>
                </ProtectedSection>
              </td>
            </tr>
          ))}

          {rows.length === 0 && (
            <tr>
              <td colSpan={5} className="text-muted">
                Aucune tranche. Ajoute une tranche puis enregistre.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Container>
  );
}